import React, { forwardRef, useEffect, useState } from 'react';
import { KeenIcon, DatePicker, LazyFormikSelect, FormikStateSelect } from '@/components';
import { Formik, Form, ErrorMessage, Field, FieldArray } from 'formik';
import { MaskedInput } from '@/components/form';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import { endOfDay, subYears } from 'date-fns';
import { FormatFileSize, getRows } from '@/utils';
import { useContactTypeOptions, useSpecialityOptions } from '@/hooks';
import contactTypeService from '@/services/contact/contactTypeService';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';

const AddReferralModal = forwardRef(({
    show,
    onClose,
    onConfirm,
    title,
    loading,
    setLoading
}, formikRef) => {
    if (!show) return null
    const [uploadedFiles, setUploadedFiles] = useState([]); // valid only
    const [rejectedFiles, setRejectedFiles] = useState([]); // [{ file, reasons: string[] }]
    const usPhoneRegex = /^\d{10}$/;
    const { locale, formatMessage } = useIntl();

    const loadContactTypeOptions = useContactTypeOptions({ defaultPageSize: 20 });
    const loadSpecialityOptions = useSpecialityOptions({ defaultPageSize: 20 });
    // Default the Contact Type dropdown to "Provider". preloadedOption gives the
    // async select a label to render before the full list is fetched.
    const [defaultContactType, setDefaultContactType] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await contactTypeService.getAllContactsType({ page: 1, pageSize: 50, search: 'Provider' });
                const provider = getRows(res).find(
                    (t) => String(t.name).toLowerCase() === 'provider'
                );
                if (active && provider) {
                    setDefaultContactType({
                        label: provider.name,
                        value: String(provider.id),
                        id: String(provider.id),
                    });
                }
            } catch {
                // Non-fatal: the user can still pick a contact type manually.
            }
        })();
        return () => { active = false; };
    }, []);


    const validationSchema = Yup.object().shape({
        first_name: Yup.string().required(formatMessage({ id: 'add_referral.first_name_required', defaultMessage: 'First Name is required' })),
        last_name: Yup.string().required(formatMessage({ id: 'add_referral.last_name_required', defaultMessage: 'Last Name is required' })),
        contact_type_id: Yup.string().nullable().required(formatMessage({ id: 'add_referral.contact_type_required', defaultMessage: 'Contact Type is required' })),
        dob: Yup.date().nullable().required(formatMessage({ id: 'add_referral.date_of_birth_required' })).max(subYears(new Date(), 18), 'Must be at least 18 years old'),
        do_injury: Yup.date().nullable().required(formatMessage({ id: 'add_referral.date_of_injury_required' })).max(endOfDay(new Date()), 'Date of injury cannot be in the future'),

        phone_number: Yup.array().of(
            Yup.object().shape({
                title: Yup.string().nullable(),
                number: Yup.string()
                    .transform(value => (value ? value.replace(/\D/g, '') : value))
                    .matches(usPhoneRegex, formatMessage({ id: 'add_referral.valid_phone_number' }))
                    .required(formatMessage({ id: 'add_referral.contact_number_required' }))
            })
        ),

        speciality: Yup.string().nullable(),
        doctor_name: Yup.string().nullable(),
        note: Yup.string().nullable(),

        address: Yup.string().required(formatMessage({ id: 'add_referral.address_required' })),
        city: Yup.string().required(formatMessage({ id: 'add_referral.city_required' })),
        state: Yup.string().required(formatMessage({ id: 'add_referral.state_required' })),
        zip: Yup.string()
            .matches(/^\d{5}(-\d{4})?$/, formatMessage({ id: 'add_referral.invalid_zip_code' }))
            .required(formatMessage({ id: 'add_referral.zip_code_required' })),

        documents: Yup.mixed().nullable()
        // documents: Yup.array()
        //     .nullable()
        //     .test(
        //         'fileValidation',
        //         'Only PDF, DOC, DOCX, XLS, XLSX files under 100MB are allowed.',
        //         (files) => {
        //             if (!files) return true;
        //             return files.every(file => {
        //                 if (!file) return true;
        //                 const isValidSize = file.size <= 100 * 1024 * 1024;
        //                 const isValidType = supportedFormats.includes(file.type);
        //                 const ext = file.name?.split('.').pop().toLowerCase();
        //                 const isValidExt = allowedExtensions.includes(ext);
        //                 return isValidSize && isValidType && isValidExt;
        //             });
        //         }
        //     )
    });


    const initialValues = {
        first_name: '',
        last_name: '',
        contact_type_id: defaultContactType?.value ?? '',
        contact_type: defaultContactType ?? null,
        dob: null,
        do_injury: null,

        phone_number: [
            {
                title: 'Contact Number',
                number: ''
            }
        ],

        address: '',
        city: '',
        state: '',
        zip: '',

        add_type: 'Home', // used in FormData but not in form fields
        speciality: null,
        doctor_name: null,
        original_doctor_name: null, // internal field to set original doctor name
        note: null,

        documents: []
    };


    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB per file
    const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100 MB total

    const ALLOWED_MIME = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/vnd.ms-excel',                                                // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',       // .xlsx
    ];

    const ALLOWED_EXT = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];

    const acceptMap = {
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    };

    const dedupeByNameAndSize = (files) => {
        const seen = new Set();
        return files.filter(f => {
            const key = `${f.name}__${f.size}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const totalSizeOf = (files) => files.reduce((acc, f) => acc + (f?.size || 0), 0);

    const onDrop = (acceptedFiles, fileRejections) => {
        const valid = [];
        const invalid = []; // { file, reasons: [] }

        // Re-check accepted for safety (type/size)
        acceptedFiles?.forEach((file) => {
            const ext = `.${(file.name?.split('.').pop() || '').toLowerCase()}`;
            const isValidType = ALLOWED_MIME.includes(file.type) || ALLOWED_EXT.includes(ext);
            const isValidSize = file.size <= MAX_FILE_SIZE;

            if (isValidType && isValidSize) {
                valid.push(file);
            } else {
                const reasons = [];
                if (!isValidType) reasons.push(formatMessage({ id: 'add_referral.invalid_file_type' }));
                if (!isValidSize) reasons.push(formatMessage({ id: 'add_referral.file_too_large' }));
                invalid.push({ file, reasons });
            }
        });

        // Fold in true rejections from dropzone (oversize/type)
        (fileRejections || []).forEach(({ file, errors }) => {
            const reasons = (errors || []).map(e => {
                if (e.code === 'file-invalid-type') return formatMessage({ id: 'add_referral.invalid_file_type' });
                if (e.code === 'file-too-large') return formatMessage({ id: 'add_referral.file_too_large' });
                return e.message || 'Rejected';
            });
            invalid.push({ file, reasons });
        });

        // Merge + dedupe valid, then enforce total size
        const mergedValid = dedupeByNameAndSize([...uploadedFiles, ...valid]);
        if (totalSizeOf(mergedValid) > MAX_TOTAL_SIZE) {
            formikRef?.current?.setFieldError(
                'documents',
                formatMessage({ id: 'add_referral.total_upload_limit_exceeded' })
            );
        } else {
            setUploadedFiles(mergedValid);
            formikRef?.current?.setFieldValue('documents', mergedValid);
            formikRef?.current?.setFieldError('documents', undefined);
        }

        // Merge + dedupe rejected (by name+size, merge reasons)
        const merged = [...rejectedFiles];
        invalid.forEach(item => {
            const idx = merged.findIndex(r => r.file.name === item.file.name && r.file.size === item.file.size);
            if (idx === -1) merged.push(item);
            else merged[idx] = {
                file: merged[idx].file,
                reasons: Array.from(new Set([...(merged[idx].reasons || []), ...(item.reasons || [])]))
            };
        });
        setRejectedFiles(merged);

        if ((invalid?.length || 0) > 0) {
            formikRef?.current?.setFieldError(
                'documents',
                formatMessage({ id: 'add_referral.files_rejected' })
            );
        }
    };

    const removeFile = (index) => {
        const updated = uploadedFiles.filter((_, i) => i !== index);
        setUploadedFiles(updated);
        formikRef?.current?.setFieldValue('documents', updated);

        if (totalSizeOf(updated) > MAX_TOTAL_SIZE) {
            formikRef?.current?.setFieldError('documents', formatMessage({ id: 'add_referral.total_upload_limit_exceeded' }));
        } else {
            formikRef?.current?.setFieldError('documents', undefined);
        }
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        multiple: true,
        accept: acceptMap,
        maxSize: MAX_FILE_SIZE, // 100MB per file
    });


    return (
        <div className='modal-wrapper modal-animate-fade'>
            <div className='modal-animate-scale h-fit max-h-[80vh] scrollable-y-auto bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark] border border-gray-300 dark:border-t-light dark:border-light p-6 rounded-lg shadow-lg w-4/5 max-w-xl relative'>
                <h3 className="capitalize card-title">{title}</h3>
                <button
                    type="button"
                    onClick={onClose}
                    className="modal-close-btn"
                >
                    <KeenIcon icon="cross" />
                </button>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={(values, { resetForm, setErrors }) => {
                        onConfirm(values, resetForm, setErrors);
                    }}
                >
                    {({ errors, touched, setErrors, setFieldValue }) => (
                        // console.error('Errors', errors),
                        <Form>
                            {/* <div className='flex items-center gap-5'> */}
                            <div className='flex items-start max-sm:flex-wrap justify-between gap-3 my-4'>
                                <div className='w-full'>
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.first_name' defaultMessage='First Name' /> <span className='pl-px text-red-600'>*</span></label>
                                    <Field name="first_name" className="input" />
                                    <ErrorMessage name="first_name" component="div" className="text-red-500 text-[11px] mt-1" />
                                </div>
                                <div className='w-full'>
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.last_name' defaultMessage='Last Name' /> <span className='pl-px text-red-600'>*</span></label>
                                    <Field name="last_name" className="input" />
                                    <ErrorMessage name="last_name" component="div" className="text-red-500 text-[11px] mt-1" />
                                </div>
                            </div>

                            <div className='flex items-start max-sm:flex-wrap justify-between gap-3 my-4'>
                                <div className="w-full">
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.contact_number' /> <span className='pl-px text-red-600'>*</span></label>
                                    <FieldArray name="phone_number">
                                        {({ form }) => (
                                            <MaskedInput
                                                className="input no-spinner"
                                                name="phone_number[0].number"
                                            />
                                        )}
                                    </FieldArray>
                                </div>
                                <div className="w-full">
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.contact_type' defaultMessage='Contact Type' /> <span className='pl-px text-red-600'>*</span></label>
                                    <LazyFormikSelect
                                        name='contact_type_id'
                                        placeholder={formatMessage({ id: 'add_referral.select_contact_type', defaultMessage: 'Select Contact Type' })}
                                        loadOptions={loadContactTypeOptions}
                                        pageSize={20}
                                        preloadedOption={defaultContactType}
                                    />
                                    <ErrorMessage name="contact_type_id" component="div" className="mt-1 text-xs text-red-500" />
                                </div>
                            </div>

                            <div className='flex items-start max-sm:flex-wrap justify-between gap-3 my-4'>
                                <div className="w-full">
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.date_of_birth' /> <span className='pl-px text-red-600'>*</span></label>
                                    <Field name="dob" component={DatePicker} isRange={false} isDob defaultPlaceholder={formatMessage({ id: 'add_referral.pick_a_date' })} clearDateTitle={formatMessage({ id: 'add_referral.clear_date' })} />
                                    <ErrorMessage name="dob" component="div" className="text-red-500 text-[11px] mt-1" />
                                </div>
                                <div className="w-full">
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.date_of_injury' /> <span className='pl-px text-red-600'>*</span></label>
                                    <Field name="do_injury" component={DatePicker} isRange={false} disableFuture defaultPlaceholder={formatMessage({ id: 'add_referral.pick_a_date' })} clearDateTitle={formatMessage({ id: 'add_referral.clear_date' })} />
                                    <ErrorMessage name="do_injury" component="div" className="text-red-500 text-[11px] mt-1" />
                                </div>
                            </div>

                            <div className='flex items-start max-sm:flex-wrap justify-between gap-3 my-4'>
                                <div className='w-full'>
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.address' /> <span className='pl-px text-red-600'>*</span></label>
                                    <Field name="address" className="input" />
                                    <ErrorMessage name="address" component="div" className="text-red-500 text-[11px] mt-1" />
                                </div>
                                <div className='w-full'>
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.city' /> <span className='pl-px text-red-600'>*</span></label>
                                    <Field name="city">
                                        {({ field, form }) => (
                                            <input
                                                {...field}
                                                className="input"
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    form.setFieldValue('city', v ? v.charAt(0).toUpperCase() + v.slice(1) : v);
                                                }}
                                            />
                                        )}
                                    </Field>
                                    <ErrorMessage name="city" component="div" className="text-red-500 text-[11px] mt-1" />
                                </div>
                            </div>

                            <div className='flex items-start max-sm:flex-wrap justify-between gap-3 my-4'>
                                <div className='w-full'>
                                    <FormikStateSelect
                                        showAsterisk={true}
                                    />
                                </div>

                                <div className='w-full'>
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.zip_code' /> <span className='pl-px text-red-600'>*</span></label>
                                    <Field name="zip" className="input" />
                                    <ErrorMessage name="zip" component="div" className="text-red-500 text-[11px] mt-1" />
                                </div>
                            </div>

                            <div className='flex items-start max-sm:flex-wrap justify-between gap-3 my-4'>
                                {/* <div className='w-full'>
                                    <label className="mb-1 form-label">Provider</label>
                                    <LazyFormikSelect
                                        name='doctor_name'
                                        options={contactOptions}
                                        placeholder="Select a Provider"
                                        onFocus={() => loadContact(null)}
                                        onOptionSelect={(selected) => {
                                            if(selected.id) {
                                                console.log('selected',selected);
                                                setFieldValue('original_doctor_name', selected.label)
                                                setFieldValue('speciality', selected.speciality)
                                            }
                                        }}
                                        isLoading={contactLoading}
                                        preloadedOption={initialValues.contact}
                                        components={{
                                            Option: CustomOption,
                                            // SingleValue: CustomSingleValue,
                                        }}
                                    />
                                    <ErrorMessage name="doctor_name" component="div" className="mt-1 text-xs text-red-500" />
                                </div> */}
                                {/* <Field name="doctor_name" className="input" /> */}
                                <div className="w-full">
                                    <label className="mb-1 form-label"><FormattedMessage id='add_referral.provider_specialty' /></label>
                                    <LazyFormikSelect
                                        name='speciality'
                                        placeholder={formatMessage({ id: 'add_referral.select_specialty' })}
                                        loadOptions={loadSpecialityOptions}
                                        customClassNames={{
                                            menuList: '!max-h-[150px]',
                                        }}
                                    />
                                    <ErrorMessage name="speciality" component="div" className="mt-1 text-xs text-red-500" />
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 form-label"><FormattedMessage id='add_referral.note' /></label>
                                <Field as="textarea" className="input pt-2 h-[120px]" name="note" />
                                <ErrorMessage name="note" component="div" className="text-red-500 text-[11px] mt-1" />
                            </div>

                            {(uploadedFiles.length > 0 || rejectedFiles.length > 0) && (
                                <div className="flex items-center flex-wrap gap-2 max-h-40 overflow-y-auto mt-2.5">
                                    {uploadedFiles.map((file, index) => (
                                        <div
                                            key={`valid-${file.name}-${file.size}-${index}`}
                                            className="md:w-[49%] w-full flex items-center justify-between border border-primary p-3 rounded-md bg-primary-light"
                                        >
                                            <div className="flex flex-col max-w-[70%]">
                                                <p className="text-sm truncate max-w-[70%]">{file.name}</p>
                                                <span className="w-fit mt-1.5 truncate badge badge-xs text-[11px] badge-outline badge-primary"><FormattedMessage id='add_referral.size' /> {FormatFileSize(file.size)}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="ml-4 text-sm underline text-danger"
                                            >
                                                <FormattedMessage id='add_referral.remove' />
                                            </button>
                                        </div>
                                    ))}

                                    {rejectedFiles.map(({ file, reasons }, index) => (
                                        <div
                                            key={`rejected-${file.name}-${file.size}-${index}`}
                                            className="w-full flex items-center justify-between p-3 rounded-md border border-danger bg-danger-light"
                                        >
                                            <div className="flex flex-col max-w-[70%]">
                                                <p className="text-sm truncate max-w-[70%] text-danger">{file.name}</p>
                                                <span className="w-fit mt-1.5 truncate badge badge-xs text-[11px] badge-outline badge-danger"><FormattedMessage id='add_referral.size' /> {FormatFileSize(file.size)}</span>
                                                {reasons?.length > 0 && (
                                                    <ul className="mt-1 text-[11px] text-danger list-disc pl-4">
                                                        {reasons.map((r, i) => <li key={i}>{r}</li>)}
                                                    </ul>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setRejectedFiles(prev => prev.filter((_, i) => i !== index))}
                                                className="ml-4 text-sm underline text-danger"
                                            >
                                                <FormattedMessage id='add_referral.remove' />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className='flex flex-col justify-center items-end mt-2.5'>
                                <div {...getRootProps()} className='flex items-center justify-between w-full'>
                                    <ErrorMessage name="documents" component="div" className="text-red-500 text-[11px] mt-1" />
                                    <input {...getInputProps()} />
                                    <button
                                        type="button"
                                        onClick={open}
                                        className="text-sm leading-none w-fit text-primary btn btn-link btn-sm"
                                    >
                                        <FormattedMessage id='add_referral.upload_document' />
                                    </button>
                                </div>
                                <button type="submit" disabled={loading || rejectedFiles.length > 0} className="justify-center w-full mt-2 btn btn-primary">
                                    {loading ? <FormattedMessage id='add_referral.submitting' /> : <FormattedMessage id='add_referral.submit' />}
                                </button>
                            </div>
                            {/* </div> */}
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    )
})

AddReferralModal.displayName = 'AddReferralModal';

export { AddReferralModal }
